import React, { useMemo } from 'react';

import FormControl from '@liquidweb-dev/undercurrent/FormControl';
import Grid from '@liquidweb-dev/undercurrent/Grid';
import RadioGroup from '@liquidweb-dev/undercurrent/RadioGroup';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import type { LWApiPublicProductFlatDetailsResultRawI } from '@liquidweb-dev/club-sauce/public/product/index.raw';
import type { AssetDetails } from 'modules/queries/asset/useDetails';
import type { FormControlProps } from '@liquidweb-dev/undercurrent/FormControl';
import Option from './QuotaOption';

export interface BackupSettingsQuotaSelectProps extends FormControlProps {
	acronisProductDetails: LWApiPublicProductFlatDetailsResultRawI;
	assetDetails: AssetDetails;
	currentQuota?: number;
	destination: number | string;
	disabled: boolean;
	onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	value: number | string;
}
const BackupSettingsQuotaSelect: React.FC<BackupSettingsQuotaSelectProps> = ({
	acronisProductDetails,
	assetDetails,
	currentQuota,
	destination,
	disabled,
	onChange,
	value,
}) => {
	const quota = useMemo(
		() =>
			acronisProductDetails?.flat_options?.find(
				(item) => item?.parent_ptov_id === destination,
			),
		[acronisProductDetails, destination],
	);

	const options = useMemo(
		() =>
			quota?.values
				?.filter(
					(item) =>
						item.public &&
						assetDetails?.region_id in (item?.region_id_prices || {}), // make sure the option is available in the current region
				)
				?.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)) || [],
		[assetDetails?.region_id, quota?.values],
	);

	const basePrice = useMemo(
		() =>
			acronisProductDetails?.region_id_prices?.[assetDetails?.region_id] ?? 0,
		[acronisProductDetails, assetDetails?.region_id],
	);

	// If the number of options is odd, exclude the last option to avoid radio tile overflow
	const pairedOptions = useMemo(
		() => options?.slice(0, options.length - (options.length % 2)),
		[options],
	);

	// If any, the remaining option is the last one which will be displayed as a bigger radio tile
	const remainingOption = useMemo(() => {
		if (options.length % 2 === 0) {
			return null;
		}
		return options[options.length - 1];
	}, [options]);

	return (
		<FormControl>
			<RadioGroup value={value} onChange={onChange}>
				<Stack spacing={200}>
					<Grid container spacing={200}>
						{pairedOptions?.map((item) => (
							<Grid key={item.ptov_id} size={6}>
								<Option
									basePrice={Number(basePrice)}
									currentQuota={currentQuota}
									disabled={disabled}
									option={item}
									regionId={assetDetails?.region_id}
									value={value}
								/>
							</Grid>
						))}
					</Grid>
					{remainingOption && (
						<Option
							basePrice={Number(basePrice)}
							currentQuota={currentQuota}
							disabled={disabled}
							option={remainingOption}
							regionId={assetDetails?.region_id}
							value={value}
						/>
					)}
				</Stack>
			</RadioGroup>
		</FormControl>
	);
};

export default BackupSettingsQuotaSelect;
